import type { BaseDocument } from '../mongo/base-document.interface';

export const SUPPLIER_TYPE = {
  INSURANCE: 'INSURANCE',
  // A supplier that leases vehicles out (SWOOP-238): the vehicle Lease-details Lessor field only lists
  // ACTIVE suppliers of this type, scoped to the vehicle's territory (SWOOP-244).
  LESSOR: 'LESSOR',
  // A supplier that sells vehicles (SWOOP-146): the Add-Vehicle Acquisition step's Vendor/supplier
  // field only lists ACTIVE suppliers of this type.
  VEHICLE_DEALER: 'VEHICLE_DEALER',
  // A workshop that services vehicles (SWOOP-245). Deliberately NOT gated by the Maintenance
  // module: a fleet team records the workshops it uses whether or not it runs work orders in
  // Hetivo, so this is an ordinary supplier type like the other three, available to every tenant.
  //
  // Kept LAST on purpose, breaking this map's alphabetical order: the suppliers action bar builds
  // its Type filter from `Object.values(SUPPLIER_TYPE)`, so insertion order IS the dropdown order,
  // and the design lists Garage after Vehicle dealer. Re-sorting this map reorders the UI.
  GARAGE: 'GARAGE',
} as const;

export type SupplierType = (typeof SUPPLIER_TYPE)[keyof typeof SUPPLIER_TYPE];

export const SUPPLIER_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;

export type SupplierStatus = (typeof SUPPLIER_STATUS)[keyof typeof SUPPLIER_STATUS];

/**
 * Whether a garage is run by the fleet team itself or contracted from outside (SWOOP-245).
 * Required when a supplier's `type` is `GARAGE`, meaningless for every other type.
 */
export const GARAGE_KIND = {
  /** Run in-house by the fleet team. */
  OWN: 'OWN',
  /** Contracted supplier, invoices the fleet. */
  OUTSIDE: 'OUTSIDE',
} as const;

export type GarageKind = (typeof GARAGE_KIND)[keyof typeof GARAGE_KIND];

export const GARAGE_KINDS = Object.values(GARAGE_KIND);

/**
 * The work a garage takes on (SWOOP-245) — used to suggest a garage when a work order is sent out.
 * A garage carries zero or more; the field is multi-select and never required.
 */
export const GARAGE_SPECIALITY = {
  AIR_CONDITIONING: 'AIR_CONDITIONING',
  ALIGNMENT: 'ALIGNMENT',
  BODYWORK: 'BODYWORK',
  DIAGNOSTICS: 'DIAGNOSTICS',
  ELECTRICAL: 'ELECTRICAL',
  GLASS: 'GLASS',
  MECHANICAL: 'MECHANICAL',
  PAINT: 'PAINT',
  SERVICING: 'SERVICING',
  TYRES: 'TYRES',
} as const;

export type GarageSpeciality = (typeof GARAGE_SPECIALITY)[keyof typeof GARAGE_SPECIALITY];

export const GARAGE_SPECIALITIES = Object.values(GARAGE_SPECIALITY);

/**
 * Full Supplier entity as stored in MongoDB.
 * A Supplier is a territory-scoped vendor directory record (SWOOP-244).
 */
export interface Supplier extends BaseDocument {
  // Optional fields are `string | null`: they can be cleared via PATCH
  // (`UpdateSupplier` sends `null`), which the update path persists with `$set`,
  // so a read-back can legitimately return `null` for a previously-set field.
  address?: string | null;
  contactPerson?: string | null;
  email: string;
  /**
   * Which kind of workshop this is (SWOOP-245). Required when `type` is `GARAGE` and cleared when the
   * type changes away from it. The requirement is conditional on a field a PATCH body may not carry,
   * so it is enforced in the SERVICE against the STORED row — see `SupplierService`.
   */
  garageKind?: GarageKind | null;
  legalName: string;
  phone: string;
  /** What this garage takes on (SWOOP-245). Garage-only; cleared when the type changes away. */
  specialities?: GarageSpeciality[] | null;
  status: SupplierStatus;
  taxId?: string | null;
  /** Owning territory. Immutable after creation — the drawer shows it as fixed. */
  territoryId: string;
  tradeName?: string | null;
  type: SupplierType;
  vatNumber?: string | null;
}

/**
 * A Supplier as returned by the SEARCH endpoint: the stored entity plus the counts the list needs but
 * the record does not store (SWOOP-245).
 */
export interface SupplierListItem extends Supplier {
  /**
   * How many live documents this supplier has — the suppliers table's Docs column. Present only for
   * GARAGE rows: no other supplier type has a Documents block, so counting them would be a query
   * whose answer is always 0.
   */
  documentCount?: number;
}

/**
 * Payload for creating a new Supplier. Optional fields are set-or-omitted on
 * create (never cleared), so they are `string | undefined` here — unlike the read
 * model, only `UpdateSupplier` carries the `null` clearing semantics.
 */
export type CreateSupplier = Omit<
  Supplier,
  | 'address'
  | 'contactPerson'
  | 'createdAt'
  | 'deletedAt'
  | 'garageKind'
  | 'id'
  | 'specialities'
  | 'taxId'
  | 'tenantId'
  | 'tradeName'
  | 'updatedAt'
  | 'vatNumber'
> & {
  address?: string;
  contactPerson?: string;
  garageKind?: GarageKind;
  specialities?: GarageSpeciality[];
  taxId?: string;
  tradeName?: string;
  vatNumber?: string;
};

/**
 * Payload for updating an existing Supplier via PATCH.
 */
export interface UpdateSupplier {
  address?: string | null;
  contactPerson?: string | null;
  email?: string;
  /** `null` clears it — what a type change away from `GARAGE` sends. */
  garageKind?: GarageKind | null;
  legalName?: string;
  phone?: string;
  /** `null` clears it — what a type change away from `GARAGE` sends. */
  specialities?: GarageSpeciality[] | null;
  status?: SupplierStatus;
  taxId?: string | null;
  tradeName?: string | null;
  type?: SupplierType;
  vatNumber?: string | null;
}
